import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, InputSuelto, Select } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  MODOS_DE_IMPORTACION, useDestinosDeImportacion, useImportar,
  type ResultadoDeImportacion,
} from './api';

/**
 * Importaciones — carga masiva con vista previa de validación.
 *
 * ## Lo que cambia
 *
 * - **Cambiar el modo ya no vuelve a subir el archivo sin avisar.**
 *   `setImportMode` llamaba a `previewImport()` si había una vista previa, así
 *   que tocar el desplegable disparaba una subida entera en silencio. Ahora la
 *   vista previa caduca visiblemente y se pide otra vez a mano.
 * - **La acción de cada fila se lee.** Era un color de fondo con alfa
 *   concatenado (`${col}1f`) y la palabra en ese mismo color: cuatro estados que
 *   solo se distinguían por el tono.
 * - **Aplicar no se puede pulsar dos veces**, y mientras aplica lo dice. El
 *   vanilla reemplazaba el contenedor por un `spinner-lg`, así que el botón
 *   desaparecía —y con él la única pista de qué se estaba haciendo—.
 * - **El archivo elegido se ve.** Era un `<input type=file>` suelto con estilos
 *   del navegador.
 */
export function Importaciones() {
  const destinos = useDestinosDeImportacion(true);
  const importar = useImportar();

  const [destino, setDestino] = useState('');
  const [modo, setModo] = useState<string>(MODOS_DE_IMPORTACION[0][0]);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [resultado, setResultado] = useState<ResultadoDeImportacion | null>(null);
  const [aplicado, setAplicado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  // Qué modo produjo la vista previa que hay en pantalla. Si cambia, la vista
  // previa ya no describe lo que pasaría.
  const [modoDeLaVista, setModoDeLaVista] = useState<string | null>(null);

  const lista = destinos.data ?? [];
  const elegido = lista.find((d) => d.key === destino) ?? lista[0];
  const claveDestino = elegido?.key ?? '';

  if (destinos.isPending) return <SkeletonFilas filas={4} columnas={2} />;
  if (destinos.isError) {
    return (
      <EstadoError
        err={destinos.error}
        que="las importaciones"
        onReintentar={() => void destinos.refetch()}
      />
    );
  }

  function correr(aplicar: boolean) {
    if (!archivo || !claveDestino) return;
    importar.mutate({ destino: claveDestino, modo, archivo, aplicar }, {
      onSuccess: (r) => {
        setResultado(r);
        setAplicado(aplicar);
        setModoDeLaVista(modo);
        if (aplicar) {
          Utils.toast(
            `Importación aplicada: ${r.created ?? 0} ${(r.created ?? 0) === 1 ? 'creado' : 'creados'}, `
            + `${r.updated ?? 0} ${(r.updated ?? 0) === 1 ? 'actualizado' : 'actualizados'}`,
            'success');
        }
      },
      onError: (e) => Utils.toast((e as Error).message, 'error'),
    });
  }

  const caducada = resultado != null && !aplicado && modoDeLaVista !== modo;

  return (
    <>
      <header className="mb-4">
        <h2 className="text-4 font-bold text-brand-ink">Motor de importaciones</h2>
        <p className="mt-1 text-3 text-muted">
          Carga masiva con vista previa de validación. Valida contra los catálogos, identifica por
          clave y actualiza sin borrar lo que no venga en el archivo.
        </p>
      </header>

      <Card className="mb-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Entidad a importar">
            <Select
              value={claveDestino}
              onChange={(e) => {
                setDestino(e.target.value);
                setResultado(null);
                setArchivo(null);
              }}
            >
              {lista.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </Select>
          </Field>
          <Field
            label="Modo"
            ayuda={caducada
              ? 'Cambiaste el modo: la vista previa de abajo es del modo anterior. Vuelve a pedirla.'
              : undefined}
          >
            <Select value={modo} onChange={(e) => setModo(e.target.value)}>
              {MODOS_DE_IMPORTACION.map(([k, l, d]) => (
                <option key={k} value={k}>{l} — {d}</option>
              ))}
            </Select>
          </Field>
        </div>

        {elegido && (
          <>
            <p className="mt-3 text-2 text-muted">{elegido.description}</p>
            <p className="mt-2 text-2 text-muted">
              <strong>Columnas:</strong>{' '}
              {/* El separador va ANTES y solo desde la segunda: ponerlo detrás de
                  cada una deja un «·» colgando al final de la lista. */}
              {elegido.fields.map((f, i) => (
                <span key={f.key} className="whitespace-nowrap">
                  {i > 0 && ' · '}
                  {f.label}
                  {(f.required || f.required_create) && (
                    <abbr title="Obligatoria" className="no-underline"> *</abbr>
                  )}
                  {f.catalog && (
                    <>
                      {' '}
                      <i
                        className="bi bi-tag"
                        aria-label={`${f.label} se valida contra el catálogo`}
                      />
                    </>
                  )}
                </span>
              ))}
            </p>
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => Utils.downloadAuthed(
              `/api/admin/imports/${claveDestino}/template`, `plantilla_${claveDestino}.xlsx`)}
          >
            <i className="bi bi-download" aria-hidden="true" /> Descargar plantilla
          </Button>

          <Field label="Archivo de Excel" className="m-0 flex-1 basis-56">
            <InputSuelto
              type="file"
              accept=".xlsx,.xls"
              aria-label="Archivo de Excel a importar"
              onChange={(e) => {
                setArchivo(e.target.files?.[0] ?? null);
                setResultado(null);
              }}
            />
          </Field>

          <Button
            size="sm"
            loading={importar.isPending && !importar.variables?.aplicar}
            disabled={!archivo}
            razonDeshabilitado="Elige primero un archivo de Excel."
            onClick={() => correr(false)}
          >
            <i className="bi bi-search" aria-hidden="true" /> Vista previa
          </Button>
        </div>
      </Card>

      {resultado && (
        <Resultado
          datos={resultado}
          aplicado={aplicado}
          caducada={caducada}
          aplicando={importar.isPending && Boolean(importar.variables?.aplicar)}
          onAplicar={() => setConfirmando(true)}
        />
      )}

      <ConfirmDialog
        abierto={confirmando}
        onCambio={setConfirmando}
        titulo="Confirmar importación"
        descripcion={`Se aplicará en modo «${MODOS_DE_IMPORTACION.find(([k]) => k === modo)?.[1] ?? modo}». Los registros nuevos se crean y los que ya existen se actualizan sin borrar datos.`}
        textoConfirmar="Aplicar importación"
        onConfirmar={() => { setConfirmando(false); correr(true); }}
      />
    </>
  );
}

const ACCIONES: Record<string, { tono: 'ok' | 'info' | 'neutral' | 'danger'; palabra: string }> = {
  create: { tono: 'ok', palabra: 'Crear' },
  update: { tono: 'info', palabra: 'Actualizar' },
  skip: { tono: 'neutral', palabra: 'Omitir' },
  error: { tono: 'danger', palabra: 'Error' },
};

function Resultado({ datos: d, aplicado, caducada, aplicando, onAplicar }: {
  datos: ResultadoDeImportacion;
  aplicado: boolean;
  caducada: boolean;
  aplicando: boolean;
  onAplicar: () => void;
}) {
  const s = d.summary ?? {
    create: d.created, update: d.updated, skip: d.skipped, error: d.errors,
  };
  const filas = d.rows ?? [];
  const hayQueHacer = (s.create ?? 0) + (s.update ?? 0) > 0;

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <strong className="flex items-center gap-2 text-3 text-brand-ink">
          {aplicado ? (
            <>
              <i className="bi bi-check-circle text-ok-ink" aria-hidden="true" /> Importación
              aplicada
            </>
          ) : (
            <><i className="bi bi-eye" aria-hidden="true" /> Vista previa</>
          )}
        </strong>
        <span className="text-2 text-muted">
          {d.total} {d.total === 1 ? 'fila' : 'filas'} · columnas:{' '}
          {(d.mapped_columns ?? []).join(', ') || '—'}
        </span>
        {aplicado && d.operation_id && (
          <span className="ml-auto font-mono text-1 text-muted">op: {d.operation_id}</span>
        )}
      </div>

      {caducada && (
        <p
          className="mb-3 rounded-sm border border-warn bg-warn/10 p-3 text-2 text-warn-ink"
          role="status"
        >
          Esta vista previa se calculó con el modo anterior. Vuelve a pedirla antes de aplicar.
        </p>
      )}

      <dl className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {([['create', aplicado ? 'Creados' : 'A crear'],
          ['update', aplicado ? 'Actualizados' : 'A actualizar'],
          ['skip', 'Omitidos'],
          ['error', 'Con error']] as const).map(([k, etiqueta]) => (
          <div key={k} className="rounded-sm border border-line bg-sunken p-3 text-center">
            <dd className="text-6 font-extrabold leading-none tabular-nums text-brand-ink">
              {s[k] ?? 0}
            </dd>
            <dt className="mt-1 text-2 text-muted">{etiqueta}</dt>
          </div>
        ))}
      </dl>

      <div className="max-h-[44vh] overflow-auto rounded-sm border border-line">
        <table className="w-full text-2">
          <caption className="sr-only">Qué pasará con cada fila del archivo</caption>
          <thead className="sticky top-0 bg-sunken">
            <tr>
              <th scope="col" className="p-2 text-left font-semibold">Fila</th>
              <th scope="col" className="p-2 text-left font-semibold">Cédula</th>
              <th scope="col" className="p-2 text-left font-semibold">Nombre</th>
              <th scope="col" className="p-2 text-left font-semibold">Acción</th>
              <th scope="col" className="p-2 text-left font-semibold">Observaciones</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((r) => {
              const a = ACCIONES[r.action] ?? { tono: 'neutral' as const, palabra: r.action };
              return (
                <tr key={r.row} className="border-t border-line">
                  <td className="p-2 tabular-nums text-muted">{r.row}</td>
                  <td className="p-2 tabular-nums">{r.cedula || '—'}</td>
                  <td className="p-2">{r.name || '—'}</td>
                  <td className="p-2"><Badge tone={a.tono}>{a.palabra}</Badge></td>
                  <td className="p-2 text-muted">{(r.messages ?? []).join('; ')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!aplicado && (
        <p className="mt-4 flex justify-end">
          <Button
            loading={aplicando}
            disabled={!hayQueHacer || caducada}
            razonDeshabilitado={caducada
              ? 'Vuelve a pedir la vista previa con el modo actual.'
              : 'No hay nada que crear ni actualizar en este archivo.'}
            onClick={onAplicar}
          >
            <i className="bi bi-cloud-arrow-up" aria-hidden="true" /> Aplicar importación
          </Button>
        </p>
      )}
    </Card>
  );
}
